import { useEffect, useReducer, useState } from 'react';
import { loadAccounts, loadDemo, type AccountLoad } from './accounts';
import { applyOverlay, subscribeClientStore } from './clientStore';

let cache: AccountLoad | null = null;

/**
 * Loads accounts once and memoizes for the session, then applies the local
 * client overlay (created / edited clients) on top so new and edited profiles
 * appear everywhere immediately.
 * @param demo when true, skip Firestore and use the bundled dataset.
 */
export function useAccounts(demo: boolean) {
  const [base, setBase] = useState<AccountLoad | null>(demo ? loadDemo() : cache);
  const [loading, setLoading] = useState(!base);
  const [error, setError] = useState<string | null>(null);
  const [, bump] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    if (demo) { setBase(loadDemo()); setLoading(false); return; }
    if (cache) { setBase(cache); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    loadAccounts()
      .then((r) => { if (!alive) return; cache = r; setBase(r); })
      .catch((e) => { if (alive) setError(e?.message || 'Failed to load accounts'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [demo]);

  // Re-render when a client is created or edited.
  useEffect(() => subscribeClientStore(bump), []);

  const data: AccountLoad | null = base ? { ...base, accounts: applyOverlay(base.accounts) } : null;
  return { data, loading, error };
}

/** Drop the cache (e.g. after sign-out) so the next load re-reads Firestore. */
export const resetAccountsCache = () => { cache = null; };
